/**
 * @module services/fileImportService
 * @description Service for importing bibliographic records from various file formats
 * Supports: BibTeX, BibLaTeX, RIS, CSL JSON, MODS, Refer/BibIX, TEI
 */

import type { ItemData, ItemType, Creator, CreatorType } from '@types';
import {
  getFieldsForItemType,
  getCreatorTypesForItemType,
  getDefaultCreatorType,
} from '@/data/itemTypes';

// =============================================================================
// TYPES
// =============================================================================

export type ImportFormat = 'bibtex' | 'ris' | 'csljson' | 'mods' | 'refer' | 'tei';

/** Tag structure for imported items */
interface ImportTag {
  tag: string;
  type?: number;
}

/** Draft item type allowing dynamic field assignment during parsing */
type ItemDataDraft = Partial<ItemData> & Record<string, unknown>;

export interface FileImportResult {
  success: boolean;
  records: Partial<ItemData>[];
  errors: string[];
  format: ImportFormat | null;
}

export interface ParsedRecord {
  item: Partial<ItemData>;
  rawData?: Record<string, unknown>;
}

// =============================================================================
// FORMAT DETECTION
// =============================================================================

/**
 * Detect the format of a bibliography file based on its content
 */
function detectFormat(content: string): ImportFormat | null {
  const trimmed = content.trim();

  // CSL JSON - starts with [ or {
  if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed) || parsed.type) {
        return 'csljson';
      }
    } catch {
      // Not valid JSON
    }
  }

  // BibTeX - starts with @ (entry type)
  if (/@\w+\s*\{/i.test(trimmed)) {
    return 'bibtex';
  }

  // RIS - starts with TY  -
  if (/^TY\s+-\s+/m.test(trimmed)) {
    return 'ris';
  }

  // MODS - XML with <mods or <modsCollection
  if (/<mods/i.test(trimmed)) {
    return 'mods';
  }

  // TEI - XML with <TEI or <teiHeader
  if (/<TEI|<teiHeader/i.test(trimmed)) {
    return 'tei';
  }

  // Refer/BibIX - starts with %
  if (/^%[A-Z]\s+/m.test(trimmed)) {
    return 'refer';
  }

  return null;
}

// =============================================================================
// BIBTEX PARSER
// =============================================================================

/**
 * Map BibTeX entry types to Zotero item types
 */
const bibtexTypeMap: Record<string, ItemType> = {
  article: 'journalArticle',
  book: 'book',
  booklet: 'book',
  inbook: 'bookSection',
  incollection: 'bookSection',
  inproceedings: 'conferencePaper',
  conference: 'conferencePaper',
  manual: 'report',
  mastersthesis: 'thesis',
  phdthesis: 'thesis',
  misc: 'document',
  online: 'webpage',
  patent: 'patent',
  proceedings: 'book',
  techreport: 'report',
  unpublished: 'manuscript',
  thesis: 'thesis',
};

/**
 * Map BibTeX fields to Zotero fields
 */
const bibtexFieldMap: Record<string, string> = {
  title: 'title',
  author: 'creators',
  editor: 'creators',
  year: 'date',
  date: 'date',
  journal: 'publicationTitle',
  booktitle: 'bookTitle',
  publisher: 'publisher',
  address: 'place',
  volume: 'volume',
  number: 'issue',
  pages: 'pages',
  doi: 'DOI',
  isbn: 'ISBN',
  issn: 'ISSN',
  url: 'url',
  abstract: 'abstractNote',
  keywords: 'tags',
  note: 'extra',
  language: 'language',
  edition: 'edition',
  series: 'series',
  school: 'university',
  institution: 'institution',
  howpublished: 'extra',
};

/**
 * Parse BibTeX content
 */
function parseBibTeX(content: string): ParsedRecord[] {
  const records: ParsedRecord[] = [];

  // Regex to match BibTeX entries
  const entryRegex = /@(\w+)\s*\{\s*([^,]*),\s*([\s\S]*?)\n\}/g;
  let match;

  while ((match = entryRegex.exec(content)) !== null) {
    const entryType = match[1].toLowerCase();
    const citeKey = match[2].trim();
    const fieldsStr = match[3];

    const itemType = bibtexTypeMap[entryType] || 'document';
    const item: ItemDataDraft = {
      itemType,
      tags: [] as ImportTag[],
      creators: [] as Creator[],
    };

    // Parse fields
    const fieldRegex = /(\w+)\s*=\s*(?:\{([^}]*)\}|"([^"]*)"|(\d+))/g;
    let fieldMatch;

    while ((fieldMatch = fieldRegex.exec(fieldsStr)) !== null) {
      const fieldName = fieldMatch[1].toLowerCase();
      const fieldValue = (fieldMatch[2] || fieldMatch[3] || fieldMatch[4] || '').trim();

      if (!fieldValue) continue;

      const zoteroField = bibtexFieldMap[fieldName];

      if (fieldName === 'author' || fieldName === 'editor') {
        const creatorType = fieldName === 'author' ? 'author' : 'editor';
        const authors = fieldValue.split(/\s+and\s+/i);

        for (const author of authors) {
          const creator = parseBibTeXAuthor(author, creatorType);
          if (creator) {
            (item.creators as Creator[]).push(creator);
          }
        }
      } else if (fieldName === 'keywords') {
        const keywords = fieldValue.split(/[,;]/);
        item.tags = keywords.map((k) => ({ tag: k.trim() })).filter((t) => t.tag);
      } else if (zoteroField && zoteroField !== 'creators') {
        item[zoteroField] = fieldValue;
      }
    }

    // Validate creators and fields
    validateCreatorTypes(item);
    validateFields(item);

    records.push({ item, rawData: { citeKey, entryType } });
  }

  return records;
}

/**
 * Parse a BibTeX author string
 */
function parseBibTeXAuthor(authorStr: string, creatorType: CreatorType): Creator | null {
  const trimmed = authorStr.trim();
  if (!trimmed) return null;

  // Check for "Last, First" format
  if (trimmed.includes(',')) {
    const [lastName, firstName] = trimmed.split(',').map((s) => s.trim());
    return { creatorType, lastName, firstName };
  }

  // "First Last" format
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { creatorType, name: parts[0] };
  }

  const lastName = parts.pop() || '';
  const firstName = parts.join(' ');
  return { creatorType, lastName, firstName };
}

// =============================================================================
// RIS PARSER
// =============================================================================

/**
 * Map RIS entry types to Zotero item types
 */
const risTypeMap: Record<string, ItemType> = {
  JOUR: 'journalArticle',
  BOOK: 'book',
  CHAP: 'bookSection',
  CONF: 'conferencePaper',
  THES: 'thesis',
  RPRT: 'report',
  MGZN: 'magazineArticle',
  NEWS: 'newspaperArticle',
  ELEC: 'webpage',
  PAT: 'patent',
  MAP: 'map',
  ART: 'artwork',
  MPCT: 'film',
  VIDEO: 'videoRecording',
  MUSIC: 'audioRecording',
  DATA: 'dataset',
  COMP: 'computerProgram',
  BILL: 'bill',
  CASE: 'case',
  STAT: 'statute',
  HEAR: 'hearing',
  GEN: 'document',
  UNPB: 'manuscript',
};

/**
 * Map RIS fields to Zotero fields
 */
const risFieldMap: Record<string, string> = {
  TI: 'title',
  T1: 'title',
  BT: 'bookTitle',
  JO: 'publicationTitle',
  JF: 'publicationTitle',
  T2: 'publicationTitle',
  PY: 'date',
  Y1: 'date',
  DA: 'date',
  AU: 'creators',
  A1: 'creators',
  A2: 'creators',
  ED: 'creators',
  PB: 'publisher',
  CY: 'place',
  VL: 'volume',
  IS: 'issue',
  SP: 'pages',
  EP: 'pages',
  DO: 'DOI',
  SN: 'ISBN',
  UR: 'url',
  AB: 'abstractNote',
  KW: 'tags',
  N1: 'extra',
  LA: 'language',
  ET: 'edition',
};

/**
 * Parse RIS content
 */
function parseRIS(content: string): ParsedRecord[] {
  const records: ParsedRecord[] = [];
  const entries = content.split(/^TY\s+-\s+/m).filter((e) => e.trim());

  for (const entry of entries) {
    const lines = entry.split('\n');
    const typeMatch = lines[0].trim();

    const itemType = risTypeMap[typeMatch] || 'document';
    const item: ItemDataDraft = {
      itemType,
      tags: [] as ImportTag[],
      creators: [] as Creator[],
    };

    let startPage = '';
    let endPage = '';

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line || line === 'ER  -') continue;

      const tagMatch = line.match(/^([A-Z][A-Z0-9])\s+-\s+(.*)$/);
      if (!tagMatch) continue;

      const [, tag, value] = tagMatch;
      const zoteroField = risFieldMap[tag];

      if (tag === 'AU' || tag === 'A1') {
        const creator = parseRISAuthor(value, 'author');
        if (creator) (item.creators as Creator[]).push(creator);
      } else if (tag === 'A2' || tag === 'ED') {
        const creator = parseRISAuthor(value, 'editor');
        if (creator) (item.creators as Creator[]).push(creator);
      } else if (tag === 'KW') {
        (item.tags as ImportTag[]).push({ tag: value.trim() });
      } else if (tag === 'SP') {
        startPage = value;
      } else if (tag === 'EP') {
        endPage = value;
      } else if (
        zoteroField &&
        zoteroField !== 'creators' &&
        zoteroField !== 'tags' &&
        zoteroField !== 'pages'
      ) {
        item[zoteroField] = value;
      }
    }

    // Combine pages
    if (startPage || endPage) {
      item.pages = endPage ? `${startPage}-${endPage}` : startPage;
    }

    validateCreatorTypes(item);
    validateFields(item);

    records.push({ item });
  }

  return records;
}

/**
 * Parse RIS author string
 */
function parseRISAuthor(authorStr: string, creatorType: CreatorType): Creator | null {
  const trimmed = authorStr.trim();
  if (!trimmed) return null;

  // RIS format is typically "Last, First"
  if (trimmed.includes(',')) {
    const [lastName, firstName] = trimmed.split(',').map((s) => s.trim());
    return { creatorType, lastName, firstName };
  }

  return { creatorType, name: trimmed };
}

// =============================================================================
// CSL JSON PARSER
// =============================================================================

/**
 * Map CSL types to Zotero item types
 */
const cslTypeMap: Record<string, ItemType> = {
  'article-journal': 'journalArticle',
  'article-magazine': 'magazineArticle',
  'article-newspaper': 'newspaperArticle',
  book: 'book',
  chapter: 'bookSection',
  dataset: 'dataset',
  document: 'document',
  manuscript: 'manuscript',
  map: 'map',
  'paper-conference': 'conferencePaper',
  patent: 'patent',
  report: 'report',
  thesis: 'thesis',
  webpage: 'webpage',
  motion_picture: 'film',
  song: 'audioRecording',
  graphic: 'artwork',
  bill: 'bill',
  legal_case: 'case',
  legislation: 'statute',
};

/**
 * Parse CSL JSON content
 */
function parseCSLJSON(content: string): ParsedRecord[] {
  const records: ParsedRecord[] = [];

  try {
    let data = JSON.parse(content);
    if (!Array.isArray(data)) {
      data = [data];
    }

    for (const entry of data) {
      const itemType = cslTypeMap[entry.type] || 'document';
      const item: Partial<ItemData> = {
        itemType,
        title: entry.title,
        tags: [],
        creators: [],
      };

      // Parse authors
      if (entry.author) {
        for (const author of entry.author) {
          (item.creators as Creator[]).push({
            creatorType: 'author',
            firstName: author.given || '',
            lastName: author.family || author.literal || '',
          });
        }
      }

      // Parse editors
      if (entry.editor) {
        for (const editor of entry.editor) {
          (item.creators as Creator[]).push({
            creatorType: 'editor',
            firstName: editor.given || '',
            lastName: editor.family || editor.literal || '',
          });
        }
      }

      // Map other fields
      if (entry.issued?.['date-parts']?.[0]) {
        const dateParts = entry.issued['date-parts'][0];
        item.date = dateParts.join('-');
      }

      if (entry['container-title']) item.publicationTitle = entry['container-title'];
      if (entry.publisher) item.publisher = entry.publisher;
      if (entry['publisher-place']) item.place = entry['publisher-place'];
      if (entry.volume) item.volume = String(entry.volume);
      if (entry.issue) item.issue = String(entry.issue);
      if (entry.page) item.pages = entry.page;
      if (entry.DOI) item.DOI = entry.DOI;
      if (entry.ISBN) item.ISBN = entry.ISBN;
      if (entry.ISSN) item.ISSN = entry.ISSN;
      if (entry.URL) item.url = entry.URL;
      if (entry.abstract) item.abstractNote = entry.abstract;
      if (entry.language) item.language = entry.language;

      validateCreatorTypes(item);
      validateFields(item);

      records.push({ item, rawData: entry });
    }
  } catch {
    // Invalid JSON - return empty records
  }

  return records;
}

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

/**
 * Validate and fix creator types for the item type
 */
function validateCreatorTypes(item: ItemDataDraft): void {
  if (!item.itemType || !item.creators) return;

  const validTypes = getCreatorTypesForItemType(item.itemType);
  const defaultType = getDefaultCreatorType(item.itemType);

  item.creators = (item.creators as Creator[]).map((creator) => {
    if (!validTypes.includes(creator.creatorType)) {
      return { ...creator, creatorType: defaultType };
    }
    return creator;
  });
}

/**
 * Validate and remove invalid fields for the item type
 */
function validateFields(item: ItemDataDraft): void {
  if (!item.itemType) return;

  const validFields = getFieldsForItemType(item.itemType);
  const systemFields = [
    'itemType',
    'key',
    'version',
    'dateAdded',
    'dateModified',
    'creators',
    'tags',
    'collections',
    'relations',
  ];

  const itemKeys = Object.keys(item);
  for (const key of itemKeys) {
    if (systemFields.includes(key) || (validFields as string[]).includes(key)) {
      continue;
    }

    const value = item[key];
    if (value !== undefined && value !== null && value !== '') {
      delete item[key];
    }
  }
}

// =============================================================================
// MAIN IMPORT FUNCTION
// =============================================================================

class FileImportService {
  /**
   * Import records from a file
   */
  async importFile(file: File): Promise<FileImportResult> {
    try {
      const content = await this.readFile(file);
      return this.importContent(content, file.name);
    } catch (error) {
      return {
        success: false,
        records: [],
        errors: [
          `Failed to read file: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ],
        format: null,
      };
    }
  }

  /**
   * Import records from text content
   */
  importContent(content: string, filename?: string): FileImportResult {
    const format = detectFormat(content);

    if (!format) {
      return {
        success: false,
        records: [],
        errors: [
          'Unable to detect file format. Supported formats: BibTeX, RIS, CSL JSON, MODS, TEI, Refer',
        ],
        format: null,
      };
    }

    try {
      let parsedRecords: ParsedRecord[] = [];

      switch (format) {
        case 'bibtex':
          parsedRecords = parseBibTeX(content);
          break;
        case 'ris':
          parsedRecords = parseRIS(content);
          break;
        case 'csljson':
          parsedRecords = parseCSLJSON(content);
          break;
        case 'mods':
        case 'tei':
        case 'refer':
          // TODO: Implement these parsers
          return {
            success: false,
            records: [],
            errors: [`${format.toUpperCase()} format is not yet fully supported`],
            format,
          };
      }

      const records = parsedRecords.map((r) => r.item);

      return {
        success: records.length > 0,
        records,
        errors: records.length === 0 ? ['No valid records found in the file'] : [],
        format,
      };
    } catch (error) {
      return {
        success: false,
        records: [],
        errors: [
          `Failed to parse ${format.toUpperCase()}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ],
        format,
      };
    }
  }

  /**
   * Read file content as text
   */
  private readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Get supported file extensions
   */
  getSupportedExtensions(): string[] {
    return ['.bib', '.bibtex', '.ris', '.json', '.xml', '.mods', '.tei', '.txt'];
  }

  /**
   * Get supported MIME types
   */
  getSupportedMimeTypes(): string[] {
    return [
      'application/x-bibtex',
      'application/x-research-info-systems',
      'application/json',
      'application/xml',
      'text/xml',
      'text/plain',
    ];
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const fileImportService = new FileImportService();
export type { FileImportService };
